import React from "react";
import { Button } from "./Button";

interface ConfirmPopupProps {
  open: boolean;
  title: string;
  message: string;
  messageTwo?: string; // Optional secondary message
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  icon?: React.ReactNode;
  confirmVariant?: "default" | "destructive";
  cancelVariant?: "default" | "destructive";
  hideCancelButton?: boolean;
  cancelDisabled?: boolean;
}

const ConfirmPopup: React.FC<ConfirmPopupProps> = ({
  open,
  title,
  message,
  messageTwo,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  icon,
  confirmVariant = "default",
  cancelVariant = "default",
  hideCancelButton = false,
  cancelDisabled = false,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 z-50">
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-lg z-50">
        {!hideCancelButton && (
          <button
            onClick={onCancel}
            disabled={cancelDisabled}
            className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
        <div className="flex flex-col items-center text-center">
          {icon ?? (
            <svg
              className="h-16 w-16 text-yellow-500"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4m0 4h.01"
              />
            </svg>
          )}
          <h2 className="mt-4 text-xl sm:text-2xl font-bold text-primary">
            {title}
          </h2>
          <p className="mt-2 text-gray-600 text-sm sm:text-base">{message}</p>
          {messageTwo && (
            <p className="mt-1 text-gray-600 text-sm sm:text-base">{messageTwo}</p>
          )}
          <div className="mt-6 w-full flex justify-end space-x-4">
            <Button
              onClick={onConfirm}
              variant={confirmVariant}
              className="w-full sm:w-auto"
            >
              {confirmText}
            </Button>
            {!hideCancelButton && (
              <Button
                onClick={onCancel}
                variant={cancelVariant}
                disabled={cancelDisabled}
                className="w-full sm:w-auto"
              >
                {cancelText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmPopup;